// src/hooks/useVisitorTracking.js

import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  useLocation,
} from "react-router-dom";

import {
  addVisitorPageView,
  sendVisitorHeartbeat,
  startVisitorSession,
} from "../services/visitorTrackingServices";

import {
  buildTrackedPage,
  clearStoredSessionToken,
  getOrCreateAttribution,
  getStoredSessionToken,
  getStoredVisitorToken,
  getTrackedPageTitle,
  getVisitorDeviceData,
  isExcludedTrackingPath,
  isRecoverableSessionError,
  storeSessionToken,
  storeVisitorToken,
} from "../utils/visitorTrackingClientUtils";

const DEFAULT_HEARTBEAT_INTERVAL_MS =
  25000;

/**
 * Évite deux créations simultanées lorsque React StrictMode
 * monte deux fois le composant en développement.
 */
let activeStartRequest = null;

export default function useVisitorTracking({
  enabled = true,

  heartbeatIntervalMs =
    DEFAULT_HEARTBEAT_INTERVAL_MS,

  excludedPathPrefixes = [],
} = {}) {
  const location = useLocation();

  const [ready, setReady] =
    useState(false);

  const [
    sessionToken,
    setSessionToken,
  ] = useState(() =>
    getStoredSessionToken(),
  );

  const [
    visitorToken,
    setVisitorToken,
  ] = useState(() =>
    getStoredVisitorToken(),
  );

  const [
    errorMessage,
    setErrorMessage,
  ] = useState("");

  const initializedRef =
    useRef(false);

  const currentPageRef =
    useRef("/");

  const lastTrackedPageRef =
    useRef("");

  const sessionTokenRef =
    useRef(
      getStoredSessionToken(),
    );

  const visitorTokenRef =
    useRef(
      getStoredVisitorToken(),
    );

  /**
   * Les changements de routes sont placés dans une file
   * afin d'éviter que deux requêtes se croisent.
   */
  const routeQueueRef =
    useRef(Promise.resolve());

  const trackingAllowed =
    enabled &&
    !isExcludedTrackingPath(
      location.pathname,
      excludedPathPrefixes,
    );

  /**
   * Met à jour les tokens dans les refs, le state
   * et les stockages du navigateur.
   */
  const persistTokens =
    useCallback(
      ({
        nextSessionToken,
        nextVisitorToken,
      }) => {
        if (nextSessionToken) {
          sessionTokenRef.current =
            nextSessionToken;

          setSessionToken(
            nextSessionToken,
          );

          storeSessionToken(
            nextSessionToken,
          );
        }

        if (nextVisitorToken) {
          visitorTokenRef.current =
            nextVisitorToken;

          setVisitorToken(
            nextVisitorToken,
          );

          storeVisitorToken(
            nextVisitorToken,
          );
        }
      },
      [],
    );

  /**
   * Supprime uniquement le token de session.
   *
   * Le visitor_token est conservé pour que la prochaine
   * session soit reconnue comme celle d'un visiteur revenant.
   */
  const resetCurrentSession =
    useCallback(() => {
      sessionTokenRef.current =
        null;

      setSessionToken(null);

      clearStoredSessionToken();
    }, []);

  /**
   * Démarre ou reprend une session.
   */
  const startOrResumeSession =
    useCallback(
      async (trackedPage) => {
        if (activeStartRequest) {
          return activeStartRequest;
        }

        const attribution =
          getOrCreateAttribution({
            pathname:
              window.location.pathname,

            search:
              window.location.search,
          });

        const deviceData =
          getVisitorDeviceData();

        const payload = {
          session_token:
            sessionTokenRef.current ||
            getStoredSessionToken(),

          visitor_token:
            visitorTokenRef.current ||
            getStoredVisitorToken(),

          page: trackedPage,

          page_title:
            getTrackedPageTitle(),

          referrer:
            attribution.referrer,

          utm_source:
            attribution.utm_source,

          utm_medium:
            attribution.utm_medium,

          utm_campaign:
            attribution.utm_campaign,

          utm_term:
            attribution.utm_term,

          utm_content:
            attribution.utm_content,

          ...deviceData,
        };

        activeStartRequest =
          startVisitorSession(
            payload,
          )
            .then((response) => {
              const data =
                response?.data || {};

              persistTokens({
                nextSessionToken:
                  data.session_token,

                nextVisitorToken:
                  data.visitor_token,
              });

              setReady(true);
              setErrorMessage("");

              return data;
            })
            .catch((error) => {
              const message =
                error?.response?.data
                  ?.error ||
                error?.response?.data
                  ?.message ||
                "Impossible de démarrer le suivi de navigation.";

              setErrorMessage(
                message,
              );

              throw error;
            })
            .finally(() => {
              activeStartRequest =
                null;
            });

        return activeStartRequest;
      },
      [persistTokens],
    );

  /**
   * Crée une nouvelle session après une expiration
   * ou la suppression d'une ancienne session.
   */
  const restartSession =
    useCallback(
      async (trackedPage) => {
        resetCurrentSession();

        const data =
          await startOrResumeSession(
            trackedPage,
          );

        lastTrackedPageRef.current =
          trackedPage;

        return data;
      },
      [
        resetCurrentSession,
        startOrResumeSession,
      ],
    );

  /**
   * Enregistre un changement de route React.
   */
  const trackPageView =
    useCallback(
      async (trackedPage) => {
        if (
          !trackedPage ||
          lastTrackedPageRef.current ===
            trackedPage
        ) {
          return;
        }

        const currentSessionToken =
          sessionTokenRef.current ||
          getStoredSessionToken();

        if (!currentSessionToken) {
          await restartSession(
            trackedPage,
          );

          return;
        }

        try {
          await addVisitorPageView({
            session_token:
              currentSessionToken,

            page: trackedPage,

            page_title:
              getTrackedPageTitle(),

            referrer:
              window.location.href,
          });

          lastTrackedPageRef.current =
            trackedPage;

          setErrorMessage("");
        } catch (error) {
          if (
            isRecoverableSessionError(
              error,
            )
          ) {
            await restartSession(
              trackedPage,
            );

            return;
          }

          const message =
            error?.response?.data
              ?.error ||
            error?.response?.data
              ?.message ||
            "Impossible d’enregistrer la page visitée.";

          setErrorMessage(message);
        }
      },
      [restartSession],
    );

  /**
   * Initialise la session puis enregistre les changements
   * de routes suivants.
   */
  useEffect(() => {
    if (!trackingAllowed) {
      return;
    }

    const trackedPage =
      buildTrackedPage(location);

    currentPageRef.current =
      trackedPage;

    routeQueueRef.current =
      routeQueueRef.current
        .then(async () => {
          /**
           * Premier chargement du tracker.
           *
           * La route /start crée déjà la première page vue,
           * il ne faut donc pas appeler /page-view ensuite.
           */
          if (
            !initializedRef.current
          ) {
            await startOrResumeSession(
              trackedPage,
            );

            initializedRef.current =
              true;

            lastTrackedPageRef.current =
              trackedPage;

            return;
          }

          await trackPageView(
            trackedPage,
          );
        })
        .catch((error) => {
          console.error(
            "Erreur dans la file de tracking des routes :",
            error,
          );
        });
  }, [
    location.pathname,
    location.search,
    trackingAllowed,
    startOrResumeSession,
    trackPageView,
  ]);

  /**
   * Envoie un heartbeat au serveur.
   */
  const sendHeartbeat =
    useCallback(async () => {
      if (!trackingAllowed) {
        return;
      }

      const currentSessionToken =
        sessionTokenRef.current ||
        getStoredSessionToken();

      if (!currentSessionToken) {
        try {
          await restartSession(
            currentPageRef.current,
          );
        } catch (error) {
          // L'erreur est déjà enregistrée par startOrResumeSession.
        }

        return;
      }

      try {
        await sendVisitorHeartbeat({
          session_token:
            currentSessionToken,

          page:
            currentPageRef.current,

          page_title:
            getTrackedPageTitle(),

          is_visible:
            document.visibilityState ===
            "visible",
        });

        setErrorMessage("");
      } catch (error) {
        if (
          isRecoverableSessionError(
            error,
          )
        ) {
          try {
            await restartSession(
              currentPageRef.current,
            );
          } catch (
            restartError
          ) {
            // L'erreur est déjà enregistrée.
          }

          return;
        }

        /**
         * Une perte momentanée de réseau ne doit pas bloquer
         * l'application publique.
         */
        console.warn(
          "Heartbeat visiteur non envoyé :",
          error,
        );
      }
    }, [
      restartSession,
      trackingAllowed,
    ]);

  /**
   * Heartbeat périodique.
   */
  useEffect(() => {
    if (!trackingAllowed) {
      return undefined;
    }

    const intervalId =
      window.setInterval(() => {
        sendHeartbeat();
      }, heartbeatIntervalMs);

    return () => {
      window.clearInterval(
        intervalId,
      );
    };
  }, [
    heartbeatIntervalMs,
    sendHeartbeat,
    trackingAllowed,
  ]);

  /**
   * Mise à jour immédiate lorsque l'onglet devient
   * visible ou invisible.
   */
  useEffect(() => {
    if (!trackingAllowed) {
      return undefined;
    }

    const handleVisibilityChange =
      () => {
        sendHeartbeat();
      };

    const handleFocus = () => {
      sendHeartbeat();
    };

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange,
    );

    window.addEventListener(
      "focus",
      handleFocus,
    );

    return () => {
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange,
      );

      window.removeEventListener(
        "focus",
        handleFocus,
      );
    };
  }, [
    sendHeartbeat,
    trackingAllowed,
  ]);

  /**
   * Nous ne terminons volontairement pas la session
   * dans beforeunload ou pagehide.
   *
   * Ces événements sont également déclenchés lors d'un
   * simple rechargement ou d'une navigation interne.
   * Le serveur déterminera l'inactivité avec le heartbeat.
   */

  return {
    ready,
    trackingAllowed,

    sessionToken,
    visitorToken,

    currentPage:
      currentPageRef.current,

    errorMessage,

    restartSession,
    sendHeartbeat,
  };
}